import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef } from "react";
import { LumenLogo } from "../LumenLogo";
import { Sparkles, ArrowRight, Smile, Clock } from "lucide-react";

export const FinalCTASection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section 
      ref={ref}
      className="section-padding relative overflow-hidden"
      data-testid="final-cta-section"
    >
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-white via-mint-whisper to-emerald-50" />
      
      {/* Decorative elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <motion.div
          className="absolute top-20 left-10 w-64 h-64 opacity-[0.03]"
          animate={{ rotate: 360 }}
          transition={{ duration: 100, repeat: Infinity, ease: "linear" }}
        >
          <svg viewBox="0 0 100 100" fill="none" stroke="#047857" strokeWidth="0.3">
            <circle cx="50" cy="50" r="45" />
            <circle cx="50" cy="50" r="30" />
            <circle cx="50" cy="50" r="15" />
            <line x1="50" y1="5" x2="50" y2="95" />
            <line x1="5" y1="50" x2="95" y2="50" />
          </svg>
        </motion.div>
      </div>

      <div className="lumen-container relative z-10">
        <div className="max-w-3xl mx-auto text-center">
          {/* Main message */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <h2 className="font-cormorant text-3xl md:text-4xl text-text-main mb-6">
              Tu mérites d'être <span className="text-emerald-700 italic">visible</span>
              <br />sans perdre ton <span className="text-emerald-700 italic">âme</span>.
            </h2>
          </motion.div>

          {/* Time saved reminder */}
          <motion.div
            className="inline-flex items-center gap-3 glass-card px-6 py-3 mb-10"
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            <Clock className="w-5 h-5 text-emerald-600" />
            <span className="font-manrope text-text-main">
              <strong className="text-emerald-700">≈ 15h/semaine</strong> récupérées pour ton vrai métier
            </span>
          </motion.div>

          {/* Price */}
          <motion.div
            className="mb-8"
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.5 }}
          >
            <span className="text-4xl md:text-5xl font-cormorant font-bold text-emerald-700">333€</span>
            <p className="font-manrope text-sm text-text-muted mt-2">Paiement unique • Accès à vie</p>
          </motion.div>

          {/* CTA Button */}
          <motion.div
            className="mb-12"
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.6 }}
          >
            <motion.a 
              href="https://gillesdelieuze.com/store-checkout-03/"
              target="_blank"
              rel="noopener noreferrer"
              className="cta-primary group text-lg px-12 py-5 pulse-glow"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              data-testid="final-cta-button"
            >
              <Sparkles className="w-5 h-5" />
              Je veux LUMEN
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
            </motion.a>
          </motion.div>

          {/* Meta message */}
          <motion.div
            className="glass-card p-6 md:p-8 max-w-xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.8 }}
          >
            <p className="font-manrope text-text-muted leading-relaxed">
              👉 Cette page a été conçue en <span className="text-emerald-700 font-semibold">5 minutes</span> avec les assistants de LUMEN.
            </p>
            <p className="font-manrope text-sm text-text-muted mt-3 italic">
              (Et non, je n'ai pas "donné mon âme à l'IA". J'ai juste arrêté de perdre du temps.)
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
